import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAxios } from '@/context/axiosContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { formatDate } from '@/utils/date'
import { Button, DataTable } from '@/components'
import { tableStyles } from '@/styles/dataTable/style'
import { TableColumn } from 'react-data-table-component'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useDialog } from '@/context/dialogContext'

// 헤더 스타일
const Header = styled.header`
  padding: 20px;
  font-size: 24px;
  font-weight: bold;
`

// 테이블 스타일
const TableContainer = styled.div`
  width: 100%;
  // max-width: 1000px;
  margin: auto;
  border-collapse: collapse;
  overflow: hidden;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 700px;
  margin-top: 20px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Thead = styled.thead`
  position: sticky;
  top: 0;
  background-color: #b6b6b6;
  z-index: 10;
`

const Th = styled.th`
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
`

const Td = styled.td`
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
`

// 데이터 없을 때 표시할 스타일
const NoDataRow = styled.tr`
  height: 50px;
  font-weight: bold;
  color: #666;
`

const StyledButton = styled.button`
  background: white;
  border: 2px solid rgb(208, 157, 48);
  color: rgb(208, 157, 48);
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: 0.3s;

  &:hover {
    background: rgb(208, 157, 48);
    color: white;
  }

  &:focus {
    background: rgb(180, 130, 40);
    color: white;
  }
`

// 툴팁 스타일
const Tooltip = styled.div`
  position: absolute;
  background: white;
  border: 1px solid #ddd;
  padding: 10px;
  display: flex;
  gap: 10px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
`

// 텍스트 툴팁 스타일 (스크롤 포함)
const TextTooltipContainer = styled(Tooltip)`
  width: 800px;
  height: 600px;
  overflow: hidden;
`

const TextTooltipContent = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-right: 5px; /* 스크롤과 내용 간격 */
`

// 버튼용 툴팁 스타일
const CompanyInfoButton = styled(StyledButton)`
  cursor: pointer;
`

const ScrapingInfoButton = styled.button`
  background: rgb(208, 157, 48);
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: 0.3s;

  &:hover {
    background: rgb(180, 130, 40);
  }

  &:focus {
    background: rgb(160, 110, 30);
  }
`

const PreWrappedText = styled.p`
  white-space: pre-wrap;
`

interface FailInfo {
  bizCd: string
  bizNm: string
  rem: string
  errDtl: string
  regDate: string
  glfPrceCd: string
  codeNm: string
}

const BatchFailList: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const batchSeq = location.state?.batchSeq
  const baseDt = location.state?.baseDt
  const batchNm = location.state?.batchNm
  const bizCd = location.state?.bizCd
  const [tooltip, setTooltip] = useState<{
    x: number
    y: number
    bizCd: string
    errDtl: string | undefined
  } | null>(null)
  const [tooltipType, setTooltipType] = useState<'button' | 'text' | null>(null) // 툴팁 종류
  const [failInfoList, setFailInfoList] = useState<FailInfo[]>([])

  const { showConfirm } = useDialog()

  const fetchFailInfoList = async () => {
    const response = await axios.post('/hist/failInfoList', {
      batchSeq: batchSeq,
      baseDt: baseDt,
      batchNm: batchNm,
      bizCd: bizCd,
    },{timeout: 20000})
    const common = response.data.common
    if (common.resultCode === 'FAILS0200') {
      setFailInfoList(response.data.data.failInfoList)
    } else {
      alert(`${common.resultCode}\n${common.resultMsg}`)
      setFailInfoList([])
    }
  }

  const goModifyPage = (bizCd: string, isBiz: boolean) => {
    if (isBiz)
      navigate(`/bizMgmt/detail`, {
        state: { bizCd: bizCd, prevPage: 'batch' },
      })
    else
      navigate(`/bizMgmt/scrpMgmt`, {
        state: { bizCd: bizCd, prevPage: 'batch' },
      })
  }

  useEffect(() => {
    fetchFailInfoList()

    const handleClickOutside = (event: MouseEvent) => {
      setTimeout(() => {
        if (
          !document
            .getElementById('tooltip-container')
            ?.contains(event.target as Node)
        ) {
          setTooltip(null)
          setTooltipType(null) // 클릭 외부 시 툴팁 종류도 초기화
        }
      }, 10)
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const columns: TableColumn<FailInfo>[] = [
    {
      name: 'No.',
      selector: (row, index) => (index as number) + 1,
    },
    {
      name: '업체명',
      selector: (row) => row.bizNm,
      grow: 1,
    },
    {
      name: '업체코드',
      selector: (row) => row.bizCd,
      width: '160px',
    },
    {
      name: '가격코드',
      selector: (row) => row.glfPrceCd,
      width: '80px',
    },
    {
      name: '가격코드명',
      selector: (row) => row.codeNm,
      width: '100px',
    },
    {
      name: '실패일시',
      selector: (row) => row.regDate,
      width: '170px',
    },
    {
      name: '실패사유',
      selector: (row) => row.rem,
      grow: 2,
    },
    {
      name: '실패상세',
      cell: (row) => (
        <StyledButton
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            setTooltip({
              x: rect.left - 700,
              y: rect.bottom,
              bizCd: row.bizCd,
              errDtl: row.errDtl,
            })
            setTooltipType('text')
          }}
        >
          상세
        </StyledButton>
      ),
      width: '80px',
    },
    {
      name: '',
      cell: (row) => (
        <StyledButton
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            setTooltip({
              x: rect.left,
              y: rect.bottom,
              bizCd: row.bizCd,
              errDtl: row.errDtl,
            })
            setTooltipType('button')
          }}
        >
          수정
        </StyledButton>
      ),
      width: '170px',
    },
  ]

  return (
    <>
      <TableContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ marginLeft: '20px', fontSize: '20px' }}>현재 실패 현황</p>
          <div style={{ marginLeft: 'auto', marginRight: '50px' }}>
            <div style={{ display: 'flex', gap: '5px' }}>
              <FlexWrapper gap="10px">
                <Button
                  onClick={() =>
                    showConfirm({
                      title: '목록으로 이동할까요?',
                      onConfirm() {
                        navigate(-1)
                      },
                    })
                  }
                  color="neverbe"
                >
                  목록
                </Button>
              </FlexWrapper>
            </div>
          </div>
        </div>

        <TableWrapper>
          <DataTable
            columns={columns}
            data={failInfoList}
            customStyles={tableStyles}
            noDataComponent={<div></div>}
          />
        </TableWrapper>
      </TableContainer>

      {/* 툴팁 */}
      {tooltip && tooltipType === 'text' && (
        <TextTooltipContainer
          id="tooltip-container"
          style={{ top: tooltip.y, left: tooltip.x }}
        >
          <TextTooltipContent>
            <PreWrappedText>${tooltip.errDtl}</PreWrappedText>
          </TextTooltipContent>
        </TextTooltipContainer>
      )}

      {tooltip && tooltipType === 'button' && (
        <Tooltip style={{ top: tooltip.y, left: tooltip.x }}>
          <CompanyInfoButton
            onClick={() => {
              goModifyPage(tooltip.bizCd, true)
            }}
          >
            업체정보
          </CompanyInfoButton>
          <ScrapingInfoButton
            onClick={() => {
              goModifyPage(tooltip.bizCd, false)
            }}
          >
            스크래핑정보
          </ScrapingInfoButton>
        </Tooltip>
      )}
    </>
  )
}

export default BatchFailList
